import React, { useState, useEffect } from "react";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, setDoc, query, where } from "firebase/firestore";
import { Field, TextArea, Toggle, ImageUpload, FormActions } from "../../components/admin/AdminFields";
import RichEditor from "../../components/admin/RichEditor";
import { uploadToCloudinary } from "../../lib/cloudinary";
import { seedDemoData, clearDemoData } from "../../lib/demoData";
import Spinner from "../../components/shared/Spinner";
import "./AdminPages.css";

/* ── Shared helpers ── */
function Panel({ title, children }) {
  return (
    <div className="admin-panel">
      <h3 className="admin-panel-title">{title}</h3>
      {children}
    </div>
  );
}
function TableWrap({ title, count, extra, children }) {
  return (
    <div className="admin-table-wrap">
      <div className="admin-table-header">
        <h3>{title} {count !== undefined && `(${count})`}</h3>
        {extra}
      </div>
      {children}
    </div>
  );
}
function EmptyRow({ cols, message }) {
  return <tr><td colSpan={cols} className="table-empty">{message}</td></tr>;
}
function ActionBtns({ onEdit, onDelete }) {
  return (
    <div className="action-btns">
      <button className="btn-tbl-edit"   onClick={onEdit}>Edit</button>
      <button className="btn-tbl-delete" onClick={onDelete}>Delete</button>
    </div>
  );
}

/* ════════════════════════════════════════
   OVERVIEW
════════════════════════════════════════ */
export function AdminOverview({ db, setTab, showToast }) {
  const [counts,   setCounts]   = useState({ episodes: 0, posts: 0, guests: 0, subscribers: 0, messages: 0 });
  const [seeding,  setSeeding]  = useState(false);
  const [clearing, setClearing] = useState(false);

  const loadCounts = () =>
    Promise.all([
      getDocs(collection(db, "episodes")),
      getDocs(collection(db, "blogPosts")),
      getDocs(collection(db, "guests")),
      getDocs(collection(db, "subscribers")),
      getDocs(collection(db, "messages")),
    ]).then(([e, b, g, s, m]) =>
      setCounts({ episodes: e.size, posts: b.size, guests: g.size, subscribers: s.size, messages: m.size })
    );

  useEffect(() => { loadCounts(); }, [db]);

  const handleSeed = async () => {
    if (!window.confirm("This will add demo episodes, blog posts, guests, breaking news, and placeholder About/Live settings. Continue?")) return;
    setSeeding(true);
    try {
      await seedDemoData(db);
      await loadCounts();
      showToast("Demo content seeded ✓ — refresh the public site to see it");
    } catch (e) {
      showToast("Seed failed: " + e.message);
    } finally { setSeeding(false); }
  };

  const handleClear = async () => {
    if (!window.confirm("This will permanently delete ALL demo content (tagged with _demo). Real content you added manually will NOT be touched. Continue?")) return;
    setClearing(true);
    try {
      const count = await clearDemoData(db);
      await loadCounts();
      showToast(`${count} demo items deleted ✓ — site is now client-ready`);
    } catch (e) {
      showToast("Clear failed: " + e.message);
    } finally { setClearing(false); }
  };

  const stats = [
    { num: counts.episodes,    lbl: "Episodes"    },
    { num: counts.posts,       lbl: "Blog Posts"  },
    { num: counts.guests,      lbl: "Guests"      },
    { num: counts.subscribers, lbl: "Subscribers" },
    { num: counts.messages,    lbl: "Messages"    },
  ];
  const actions = [
    ["🎬 Add Episode",    "episodes"],
    ["✍️ Write Post",     "blog"    ],
    ["👥 Add Guest",      "guests"  ],
    ["🔴 Go Live",        "live"    ],
    ["⚡ Breaking News",  "breaking"],
  ];

  return (
    <>
      <div className="stats-grid">
        {stats.map((s) => (
          <div key={s.lbl} className="stat-card">
            <div className="stat-num">{s.num}</div>
            <div className="stat-lbl">{s.lbl}</div>
          </div>
        ))}
      </div>

      <Panel title="Quick Actions">
        <div className="quick-actions">
          {actions.map(([l, t]) => (
            <button key={t} className="btn-primary" onClick={() => setTab(t)}>{l}</button>
          ))}
        </div>
      </Panel>

      <Panel title="🧪 Demo Content">
        <p className="panel-desc">
          Populate the site with realistic sample content for client previews, then wipe it all in one click when you're ready to hand over.
          Only documents tagged as demo will be removed — any real content you've added manually is safe.
        </p>
        <div className="demo-actions">
          <button className="btn-seed" onClick={handleSeed} disabled={seeding || clearing}>
            {seeding ? <><Spinner size="sm" /> &nbsp;Seeding…</> : "🌱 Seed Demo Content"}
          </button>
          <button className="btn-clear" onClick={handleClear} disabled={seeding || clearing}>
            {clearing ? <><Spinner size="sm" /> &nbsp;Clearing…</> : "🗑️ Clear All Demo Content"}
          </button>
        </div>
        <p className="demo-note">Demo content includes: 5 episodes · 4 blog posts · 5 guests · 3 breaking news items · About page · Live settings</p>
      </Panel>
    </>
  );
}

/* ════════════════════════════════════════
   EPISODES
════════════════════════════════════════ */
const BLANK_EP = { title: "", description: "", youtubeUrl: "", spotifyUrl: "", audiomackUrl: "", guests: [], date: "", topic: "" };

/* Guest chip multi-select — picks from registered guests */
function GuestPicker({ selected = [], onChange, roster = [] }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const available = roster.filter(
    (g) => !selected.includes(g) && g.toLowerCase().includes(search.toLowerCase())
  );

  const add    = (name) => { onChange([...selected, name]); setSearch(""); setOpen(false); };
  const remove = (name) => onChange(selected.filter((g) => g !== name));

  return (
    <div className="field-group">
      <label className="field-label">Guests</label>

      {/* Selected chips */}
      <div className="guest-chips">
        {selected.map((name) => (
          <span key={name} className="guest-chip">
            👤 {name}
            <button type="button" className="guest-chip-remove" onClick={() => remove(name)}>✕</button>
          </span>
        ))}
        {selected.length === 0 && <span className="guest-chips-empty">No guests selected</span>}
      </div>

      {/* Dropdown trigger */}
      <div className="guest-picker-wrap">
        <button
          type="button"
          className="guest-picker-btn"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? "▲ Close" : "▼ Add guest from list"}
        </button>
        {open && (
          <div className="guest-picker-dropdown">
            <input
              className="guest-picker-search"
              type="text"
              placeholder="Search guests…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
            {roster.length === 0 ? (
              <div className="guest-picker-empty">No guests registered yet. Add guests in the Guests tab first.</div>
            ) : available.length === 0 ? (
              <div className="guest-picker-empty">
                {search ? "No match found." : "All guests already selected."}
              </div>
            ) : (
              available.map((name) => (
                <button
                  key={name}
                  type="button"
                  className="guest-picker-option"
                  onClick={() => add(name)}
                >
                  👤 {name}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function AdminEpisodes({ db, showToast }) {
  const [episodes,    setEpisodes]    = useState([]);
  const [guestRoster, setGuestRoster] = useState([]); // names from guests collection
  const [form,        setForm]        = useState(BLANK_EP);
  const [editing,     setEditing]     = useState(null);
  const [saving,      setSaving]      = useState(false);

  const load = () =>
    getDocs(collection(db, "episodes")).then((s) =>
      setEpisodes(s.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

  // Load guest roster from Firestore guests collection
  const loadRoster = () =>
    getDocs(collection(db, "guests")).then((s) =>
      setGuestRoster(s.docs.map((d) => d.data().name).filter(Boolean).sort())
    );

  useEffect(() => { load(); loadRoster(); }, []);

  const f = (key) => (val) => setForm((p) => ({ ...p, [key]: val }));

  const save = async () => {
    if (!form.title) return showToast("Title is required");
    setSaving(true);
    try {
      if (editing) { await updateDoc(doc(db, "episodes", editing), form); showToast("Episode updated ✓"); }
      else         { await addDoc(collection(db, "episodes"), { ...form, createdAt: Date.now() }); showToast("Episode added ✓"); }
      setForm(BLANK_EP); setEditing(null); load();
    } finally { setSaving(false); }
  };

  const startEdit = (ep) => {
    // Normalise: if old doc has guestName string, migrate to guests array
    const guests = Array.isArray(ep.guests) && ep.guests.filter(Boolean).length > 0
      ? ep.guests.filter(Boolean)
      : ep.guestName ? [ep.guestName] : [];
    setForm({ ...ep, guests });
    setEditing(ep.id);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this episode?")) return;
    await deleteDoc(doc(db, "episodes", id)); load(); showToast("Deleted");
  };

  return (
    <>
      <Panel title={editing ? "Edit Episode" : "Add New Episode"}>
        <Field label="Episode Title *" value={form.title} onChange={f("title")} placeholder="Episode title" />

        <GuestPicker
          selected={form.guests || []}
          onChange={(g) => setForm((p) => ({ ...p, guests: g }))}
          roster={guestRoster}
        />

        <div className="form-row">
          <Field label="YouTube URL" value={form.youtubeUrl} onChange={f("youtubeUrl")} placeholder="https://youtube.com/watch?v=..." />
          <Field label="Spotify URL (optional)" value={form.spotifyUrl} onChange={f("spotifyUrl")} placeholder="https://open.spotify.com/episode/..." />
        </div>
        <div className="form-row">
          <Field label="Audiomack URL (optional)" value={form.audiomackUrl} onChange={f("audiomackUrl")} placeholder="https://audiomack.com/your-show/song/episode-title" />
        </div>
        <div className="form-row">
          <Field label="Date" type="date" value={form.date} onChange={f("date")} />
          <Field label="Topic / Category" value={form.topic} onChange={f("topic")} placeholder="Law Enforcement, Politics…" />
        </div>
        <TextArea label="Description" value={form.description} onChange={f("description")} placeholder="Episode description…" />
        <FormActions
          onSubmit={save}
          saving={saving}
          onCancel={editing ? () => { setForm(BLANK_EP); setEditing(null); } : undefined}
          submitLabel={editing ? "Update Episode" : "Add Episode"}
        />
      </Panel>

      <TableWrap title="All Episodes" count={episodes.length}>
        <table>
          <thead><tr><th>Title</th><th>Guests</th><th>Topic</th><th>Date</th><th>Actions</th></tr></thead>
          <tbody>
            {episodes.length === 0 && <EmptyRow cols={5} message="No episodes yet." />}
            {episodes.map((ep) => (
              <tr key={ep.id}>
                <td className="td-bold">{ep.title}</td>
                <td className="td-muted">{Array.isArray(ep.guests) && ep.guests.filter(Boolean).length > 0 ? ep.guests.filter(Boolean).join(", ") : ep.guestName || "—"}</td>
                <td><span className="badge-tag">{ep.topic || "—"}</span></td>
                <td className="td-muted">{ep.date || "—"}</td>
                <td><ActionBtns onEdit={() => startEdit(ep)} onDelete={() => del(ep.id)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrap>
    </>
  );
}



/* ════════════════════════════════════════
   BLOG
════════════════════════════════════════ */
const BLANK_POST = { title: "", excerpt: "", body: "", category: "", author: "Kedrick Scribner", coverUrl: "", status: "draft", date: "" };

export function AdminBlog({ db, showToast }) {
  const [posts,     setPosts]     = useState([]);
  const [form,      setForm]      = useState(BLANK_POST);
  const [editing,   setEditing]   = useState(null);
  const [showForm,  setShowForm]  = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving,    setSaving]    = useState(false);

  const load = () =>
    getDocs(collection(db, "blogPosts")).then((s) =>
      setPosts(s.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => b.createdAt - a.createdAt))
    );
  useEffect(() => { load(); }, []);

  const f = (key) => (val) => setForm((p) => ({ ...p, [key]: val }));

  const handleCover = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try   { const url = await uploadToCloudinary(file); setForm((p) => ({ ...p, coverUrl: url })); showToast("Cover uploaded ✓"); }
    catch { showToast("Image upload failed. Please try again."); }
    setUploading(false);
  };

  const save = async () => {
    if (!form.title) return showToast("Title is required");
    setSaving(true);
    try {
      if (editing) { await updateDoc(doc(db, "blogPosts", editing), form); showToast("Post updated ✓"); }
      else         { await addDoc(collection(db, "blogPosts"), { ...form, createdAt: Date.now() }); showToast("Post saved ✓"); }
      setForm(BLANK_POST); setEditing(null); setShowForm(false); load();
    } finally { setSaving(false); }
  };
  const startEdit = (p) => { setForm(p); setEditing(p.id); setShowForm(true); };
  const del = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await deleteDoc(doc(db, "blogPosts", id)); load(); showToast("Deleted");
  };

  return (
    <>
      {!showForm && (
        <div style={{ marginBottom: "1.5rem" }}>
          <button className="btn-primary" onClick={() => { setForm(BLANK_POST); setEditing(null); setShowForm(true); }}>
            ✍️ Write New Post
          </button>
        </div>
      )}

      {showForm && (
        <Panel title={editing ? "Edit Post" : "New Blog Post"}>
          <div className="form-row">
            <Field label="Post Title *" value={form.title}  onChange={f("title")}  placeholder="Article title" />
            <Field label="Author"       value={form.author} onChange={f("author")} placeholder="Author name" />
          </div>
          <div className="form-row">
            <Field label="Category" value={form.category} onChange={f("category")} placeholder="Law Enforcement, Politics…" />
            <Field label="Publish Date" type="date" value={form.date} onChange={f("date")} />
          </div>
          <TextArea label="Excerpt (shown on blog card)" value={form.excerpt} onChange={f("excerpt")} placeholder="Short summary…" minHeight={70} />
          <ImageUpload label="Cover Image" previewUrl={form.coverUrl} uploading={uploading} inputId="blog-cover" onFileChange={handleCover} />
          <div className="field-group">
            <label className="field-label">Article Body</label>
            <RichEditor value={form.body} onChange={f("body")} />
          </div>
          <div className="publish-row">
            <Toggle checked={form.status === "published"} onChange={(v) => setForm((p) => ({ ...p, status: v ? "published" : "draft" }))} labelOn="🟢 Published" labelOff="🟡 Draft" />
            <FormActions
              onSubmit={save}
              saving={saving}
              onCancel={() => { setForm(BLANK_POST); setEditing(null); setShowForm(false); }}
              submitLabel={editing ? "Update Post" : "Save Post"}
            />
          </div>
        </Panel>
      )}

      <TableWrap title="All Posts" count={posts.length}>
        <table>
          <thead><tr><th>Title</th><th>Author</th><th>Category</th><th>Status</th><th>Date</th><th>Actions</th></tr></thead>
          <tbody>
            {posts.length === 0 && <EmptyRow cols={6} message="No posts yet." />}
            {posts.map((p) => (
              <tr key={p.id}>
                <td className="td-bold">{p.title}</td>
                <td className="td-muted">{p.author || "—"}</td>
                <td className="td-muted">{p.category || "—"}</td>
                <td><span className={`badge-tag ${p.status === "published" ? "badge-published" : "badge-draft"}`}>{p.status || "draft"}</span></td>
                <td className="td-muted">{p.date || "—"}</td>
                <td><ActionBtns onEdit={() => startEdit(p)} onDelete={() => del(p.id)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrap>
    </>
  );
}

/* ════════════════════════════════════════
   GUESTS
════════════════════════════════════════ */
const BLANK_GUEST = { name: "", title: "", bio: "", episodeTitle: "", photoUrl: "" };

export function AdminGuests({ db, showToast }) {
  const [guests,    setGuests]    = useState([]);
  const [form,      setForm]      = useState(BLANK_GUEST);
  const [editing,   setEditing]   = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving,    setSaving]    = useState(false);

  const load = () => getDocs(collection(db, "guests")).then((s) => setGuests(s.docs.map((d) => ({ id: d.id, ...d.data() }))));
  useEffect(() => { load(); }, []);

  const f = (key) => (val) => setForm((p) => ({ ...p, [key]: val }));

  const handlePhoto = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try   { const url = await uploadToCloudinary(file); setForm((p) => ({ ...p, photoUrl: url })); showToast("Photo uploaded ✓"); }
    catch { showToast("Image upload failed. Please try again."); }
    setUploading(false);
  };

  const save = async () => {
    if (!form.name) return showToast("Name is required");
    setSaving(true);
    try {
      if (editing) { await updateDoc(doc(db, "guests", editing), form); showToast("Guest updated ✓"); }
      else         { await addDoc(collection(db, "guests"), { ...form, createdAt: Date.now() }); showToast("Guest added ✓"); }
      setForm(BLANK_GUEST); setEditing(null); load();
    } finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this guest?")) return;
    await deleteDoc(doc(db, "guests", id)); load(); showToast("Deleted");
  };

  return (
    <>
      <Panel title={editing ? "Edit Guest" : "Add New Guest"}>
        <div className="form-row">
          <Field label="Full Name *" value={form.name}  onChange={f("name")}  placeholder="Guest full name" />
          <Field label="Title / Role" value={form.title} onChange={f("title")} placeholder="e.g. Civil Rights Attorney" />
        </div>
        <Field label="Episode They Appeared In" value={form.episodeTitle} onChange={f("episodeTitle")} placeholder="Episode title" />
        <TextArea label="Bio" value={form.bio} onChange={f("bio")} placeholder="Guest bio…" />
        <ImageUpload label="Guest Photo" previewUrl={form.photoUrl} uploading={uploading} inputId="guest-photo" onFileChange={handlePhoto} />
        <FormActions
          onSubmit={save}
          saving={saving}
          onCancel={editing ? () => { setForm(BLANK_GUEST); setEditing(null); } : undefined}
          submitLabel={editing ? "Update Guest" : "Add Guest"}
        />
      </Panel>

      <TableWrap title="All Guests" count={guests.length}>
        <table>
          <thead><tr><th>Photo</th><th>Name</th><th>Title</th><th>Episode</th><th>Actions</th></tr></thead>
          <tbody>
            {guests.length === 0 && <EmptyRow cols={5} message="No guests yet." />}
            {guests.map((g) => (
              <tr key={g.id}>
                <td>{g.photoUrl ? <img src={g.photoUrl} className="td-avatar" alt="" /> : <span>👤</span>}</td>
                <td className="td-bold">{g.name}</td>
                <td className="td-muted">{g.title || "—"}</td>
                <td className="td-muted">{g.episodeTitle || "—"}</td>
                <td><ActionBtns onEdit={() => { setForm(g); setEditing(g.id); }} onDelete={() => del(g.id)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrap>
    </>
  );
}

/* ════════════════════════════════════════
   LIVE STREAM
════════════════════════════════════════ */
export function AdminLive({ db, showToast }) {
  const [d,       setD]       = useState({ isLive: false, streamUrl: "", nextShowDate: "", nextShowTitle: "", chatUrl: "" });
  const [errors,  setErrors]  = useState({});
  const [saving,  setSaving]  = useState(false);

  const f = (key) => (val) => {
    setD((p) => ({ ...p, [key]: val }));
    // Clear the error for this field as soon as user starts typing
    if (errors[key]) setErrors((e) => ({ ...e, [key]: false }));
  };

  useEffect(() => {
    getDocs(collection(db, "settings")).then((s) => {
      const x = s.docs.find((d) => d.id === "live");
      if (x) setD(x.data());
    });
  }, [db]);

  const REQUIRED = [
    { key: "streamUrl",    label: "YouTube Live URL"   },
    { key: "nextShowDate", label: "Next Show Date & Time" },
    { key: "nextShowTitle",label: "Next Show Title"    },
  ];

  const save = async () => {
    // Validate all required fields
    const newErrors = {};
    const missing   = [];
    REQUIRED.forEach(({ key, label }) => {
      if (!d[key] || !d[key].toString().trim()) {
        newErrors[key] = true;
        missing.push(label);
      }
    });

    if (missing.length > 0) {
      setErrors(newErrors);
      showToast(`Please fill in: ${missing.join(", ")}`);
      return;
    }

    setSaving(true);
    try {
      await setDoc(doc(db, "settings", "live"), d);
      showToast("Live settings saved ✓");
      setErrors({});
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel title="Live Stream Settings">
      <div style={{ marginBottom: "1.5rem" }}>
        <label className="field-label">Show Status</label>
        <Toggle checked={d.isLive} onChange={f("isLive")} labelOn="🔴 WE ARE LIVE" labelOff="⚫ OFF AIR" />
      </div>

      <div className={`field-group${errors.streamUrl ? " field-error" : ""}`}>
        <label className="field-label">
          YouTube Live URL <span className="field-required">*</span>
        </label>
        <input
          className={`field-input${errors.streamUrl ? " input-error" : ""}`}
          type="text"
          value={d.streamUrl}
          onChange={(e) => f("streamUrl")(e.target.value)}
          placeholder="https://youtube.com/watch?v=LIVE_ID"
        />
        {errors.streamUrl && <p className="field-error-msg">This field is required</p>}
      </div>

      <Field label="Live Chat URL (optional)" value={d.chatUrl} onChange={f("chatUrl")} placeholder="https://youtube.com/live_chat?v=..." />

      <div className="form-row">
        <div className={`field-group${errors.nextShowDate ? " field-error" : ""}`}>
          <label className="field-label">
            Next Show Date & Time <span className="field-required">*</span>
          </label>
          <input
            className={`field-input${errors.nextShowDate ? " input-error" : ""}`}
            type="datetime-local"
            value={d.nextShowDate}
            onChange={(e) => f("nextShowDate")(e.target.value)}
          />
          {errors.nextShowDate && <p className="field-error-msg">This field is required</p>}
        </div>

        <div className={`field-group${errors.nextShowTitle ? " field-error" : ""}`}>
          <label className="field-label">
            Next Show Title <span className="field-required">*</span>
          </label>
          <input
            className={`field-input${errors.nextShowTitle ? " input-error" : ""}`}
            type="text"
            value={d.nextShowTitle}
            onChange={(e) => f("nextShowTitle")(e.target.value)}
            placeholder="e.g. Police Reform Deep Dive"
          />
          {errors.nextShowTitle && <p className="field-error-msg">This field is required</p>}
        </div>
      </div>

      <FormActions onSubmit={save} saving={saving} submitLabel="Save Live Settings" />
    </Panel>
  );
}


/* ════════════════════════════════════════
   BREAKING NEWS
════════════════════════════════════════ */
export function AdminBreakingNews({ db, showToast }) {
  const [items, setItems] = useState([]);
  const [text,  setText]  = useState("");

  const load = () => getDocs(collection(db, "breakingNews")).then((s) => setItems(s.docs.map((d) => ({ id: d.id, ...d.data() }))));
  useEffect(() => { load(); }, [db]);

  const add = async () => {
    if (!text.trim()) return showToast("Enter a headline");
    await addDoc(collection(db, "breakingNews"), { text: text.trim(), createdAt: Date.now() });
    showToast("Breaking headline added ✓"); setText(""); load();
  };
  const del = async (id) => { await deleteDoc(doc(db, "breakingNews", id)); load(); showToast("Removed"); };

  return (
    <>
      <Panel title="Add Custom Breaking Headline">
        <p className="panel-desc">Custom headlines appear on the bottom ticker in gold alongside live RSS headlines.</p>
        <div className="breaking-input-row">
          <input
            className="field-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="Type your breaking news headline…"
          />
          <button className="btn-primary" onClick={add}>Add Headline</button>
        </div>
      </Panel>

      <TableWrap title="Custom Headlines" count={items.length}>
        <table>
          <thead><tr><th>Headline</th><th>Added</th><th>Action</th></tr></thead>
          <tbody>
            {items.length === 0 && <EmptyRow cols={3} message="No custom headlines yet." />}
            {items.map((item) => (
              <tr key={item.id}>
                <td className="td-gold">⚡ {item.text}</td>
                <td className="td-muted">{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "—"}</td>
                <td><button className="btn-tbl-delete" onClick={() => del(item.id)}>Remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrap>
    </>
  );
}

/* ════════════════════════════════════════
   ABOUT / BIO
════════════════════════════════════════ */
export function AdminAbout({ db, showToast }) {
  const [d, setD] = useState({ name: "Kedrick Scribner", tagline: "", bio: "", bio2: "", photoUrl: "", youtube: "", spotify: "", facebook: "", instagram: "", twitter: "", yearsExperience: "" });
  const [uploading, setUploading] = useState(false);
  const f = (key) => (val) => setD((p) => ({ ...p, [key]: val }));

  useEffect(() => {
    getDocs(collection(db, "settings")).then((s) => {
      const x = s.docs.find((d) => d.id === "about"); if (x) setD(x.data());
    });
  }, [db]);

  const handlePhoto = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try   { const url = await uploadToCloudinary(file); setD((p) => ({ ...p, photoUrl: url })); showToast("Photo uploaded ✓"); }
    catch { showToast("Image upload failed. Please try again."); }
    setUploading(false);
  };
  const [saving, setSaving] = useState(false);
  const save = async () => { setSaving(true); try { await setDoc(doc(db, "settings", "about"), d); showToast("About page updated ✓"); } finally { setSaving(false); } };

  return (
    <Panel title="About / Host Bio">
      <div className="form-row">
        <Field label="Host Name" value={d.name}    onChange={f("name")}    />
        <Field label="Tagline"   value={d.tagline} onChange={f("tagline")} placeholder="e.g. The Truth Teller" />
      </div>
      <TextArea label="Bio Paragraph 1" value={d.bio}  onChange={f("bio")}  placeholder="First bio paragraph…" />
      <TextArea label="Bio Paragraph 2" value={d.bio2} onChange={f("bio2")} placeholder="Second bio paragraph…" />
      <Field label="Years of Experience" value={d.yearsExperience} onChange={f("yearsExperience")} placeholder="20+" />
      <ImageUpload label="Host Photo" previewUrl={d.photoUrl} uploading={uploading} inputId="about-photo" onFileChange={handlePhoto} />
      <div className="section-divider">Social Media Links</div>
      <div className="form-row">
        <Field label="YouTube"  value={d.youtube}  onChange={f("youtube")}  placeholder="https://youtube.com/@..." />
        <Field label="Spotify"  value={d.spotify}  onChange={f("spotify")}  placeholder="https://open.spotify.com/show/..." />
      </div>
      <div className="form-row">
        <Field label="Facebook"  value={d.facebook}  onChange={f("facebook")}  />
        <Field label="Instagram" value={d.instagram} onChange={f("instagram")} />
      </div>
      <Field label="X / Twitter" value={d.twitter} onChange={f("twitter")} />
      <FormActions onSubmit={save} saving={saving} submitLabel="Save About Page" />
    </Panel>
  );
}

/* ════════════════════════════════════════
   NEWSLETTER
════════════════════════════════════════ */
export function AdminNewsletter({ db }) {
  const [subs, setSubs] = useState([]);
  useEffect(() => {
    getDocs(collection(db, "subscribers")).then((s) => setSubs(s.docs.map((d) => ({ id: d.id, ...d.data() }))));
  }, [db]);

  const exportCSV = () => {
    const csv = "Email,Date\n" + subs.map((s) => `${s.email},${s.createdAt ? new Date(s.createdAt).toLocaleDateString() : ""}`).join("\n");
    const a = document.createElement("a");
    a.href = "data:text/csv," + encodeURIComponent(csv);
    a.download = "subscribers.csv";
    a.click();
  };

  return (
    <TableWrap title="Newsletter Subscribers" count={subs.length} extra={<button className="btn-tbl-edit" onClick={exportCSV}>Export CSV</button>}>
      <table>
        <thead><tr><th>Email</th><th>Date Signed Up</th></tr></thead>
        <tbody>
          {subs.length === 0 && <EmptyRow cols={2} message="No subscribers yet." />}
          {subs.map((s) => (
            <tr key={s.id}>
              <td className="td-bold">{s.email}</td>
              <td className="td-muted">{s.createdAt ? new Date(s.createdAt).toLocaleDateString() : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}

/* ════════════════════════════════════════
   CONTACT MESSAGES
════════════════════════════════════════ */
export function AdminContactMsgs({ db }) {
  const [msgs, setMsgs] = useState([]);
  useEffect(() => {
    getDocs(collection(db, "messages")).then((s) =>
      setMsgs(s.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => b.createdAt - a.createdAt))
    );
  }, [db]);

  return (
    <TableWrap title="Contact Messages" count={msgs.length}>
      <table>
        <thead><tr><th>Name</th><th>Email</th><th>Subject</th><th>Message</th><th>Date</th></tr></thead>
        <tbody>
          {msgs.length === 0 && <EmptyRow cols={5} message="No messages yet." />}
          {msgs.map((m) => (
            <tr key={m.id}>
              <td className="td-bold">{m.name}</td>
              <td className="td-muted">{m.email}</td>
              <td>{m.subject || "—"}</td>
              <td className="td-truncate">{m.message}</td>
              <td className="td-muted">{m.createdAt ? new Date(m.createdAt).toLocaleDateString() : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}
